import { faArrowDown, faArrowUp, faEquals, faHistory, faSpinner } from "@fortawesome/free-solid-svg-icons"
import { t } from "i18next"
import riskStore from "../../model/risk.store"
import { useAppSelector } from "@/core/root-reducer"
import { Risk } from "../../model/risk.types"
import Chip from "@/components/chip"
import ListCell from "@/components/list-cell"
import ListFields from "@/components/list-fields"
import ListItem from "@/components/list-item"
import { format } from "date-fns"
import { fr } from "date-fns/locale"
import Space from "@/components/space"
import { SlideIn } from "@/utils/animations.utils"
import Button from "@/components/button"
import Highlighter from "react-highlight-words"
import { RiskDotScale } from "../RiskDotScale"
import { useRiskHistory } from "../../hooks/useRiskHistory"
import { Session } from "@/redux/_session.types"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import organizationStore from "@/features/adm/entities/organization/model/organization.store"

interface Props {
  onClose: () => void
}

export default function RiskHistorySideNav({ onClose }: Props) {
  const risk = useAppSelector(riskStore.selectors.active)
  const _session: Session = useAppSelector((s) => s._session)
  const organization = useAppSelector(organizationStore.selectors.active)

  const establishmentId = new URLSearchParams(window.location.search).get("establishmentId") ?? _session.establishmentId

  const { history, loading } = useRiskHistory(risk.id, establishmentId)

  function getTrendIcon(curr: Risk, prev?: Risk) {
    if (!prev) return faEquals
    const delta = (curr.valueEffective ?? 0) - (prev?.valueEffective ?? 0)
    if (delta < 0) return faArrowUp
    if (delta > 0) return faArrowDown
    return faEquals
  }

  function getTrendColor(curr: Risk, prev?: Risk) {
    if (!prev) return "text-gray-400"
    const delta = (curr.valueEffective ?? 0) - (prev?.valueEffective ?? 0)
    if (delta < 0) return "text-green-500"
    if (delta > 0) return "text-red-500"
    return "text-gray-400"
  }

  // Highlight text diff for attributes
  const renderDiff = (label: string, curr: any, prevVal: any, highlight = true) => {
    if (curr && typeof curr === "object") return null
    const currStr = String(curr ?? "")
    const prevStr = String(prevVal ?? "")

    if (!currStr.trim() && !prevStr.trim()) return null
    if (currStr === prevStr) return null

    return (
      <div key={label} className="mb-1">
        <b>{t(label)} : </b>
        {highlight ? (
          <Highlighter
            searchWords={currStr.split(/\s+/).filter((w) => !prevStr.includes(w))}
            autoEscape={true}
            textToHighlight={currStr}
            highlightStyle={{ backgroundColor: "#fef08a", fontWeight: 600 }}
          />
        ) : (
          <span>{currStr}</span>
        )}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30 py-8">
      <SlideIn isLeft={false}>
        <div
          className="h-fit max-h-[85vh] bg-white rounded-tl-lg rounded-bl-lg shadow-[0_4px_24px_rgba(0,0,0,0.1)] mb-7 relative 
                    flex flex-col w-fit max-w-[95vw] overflow-auto">
          {/* Header */}
          <div className="flex px-[30px] py-[20px]">
            <div className="flex flex-col">
              <div className="bold text-xl">
                <FontAwesomeIcon icon={faHistory} />
                &nbsp;
                {t("risk_history")}
              </div>
              <div className="text-gray-500 mt-2">
                <b>{risk.name}</b>
              </div>
            </div>
            <Space />
            <div className="grey-t text-sm">
              {risk.dateValidatedLabel && <div className="mb-2">Validé le {risk.dateValidatedLabel}</div>}
            </div>
          </div>

          {/* Contenu scrollable */}
          <div className="flex-1 overflow-auto pb-[40px]">
            <div className="w-fit min-w-full text-sm">
              <ListFields className="!py-2 font-bold">
                <div className="flex flex-1">
                  <ListCell width={140}>{t("Date de validation")}</ListCell>
                  <ListCell width={618}>{t("Mise à jour")}</ListCell>
                  <ListCell>{t("F")}</ListCell>
                  <ListCell>{t("G")}</ListCell>
                  <ListCell>{t("P")}</ListCell>
                  <ListCell width={140}>{t("Risque brut")}</ListCell>
                  <ListCell width={150}>{t("Risque résiduel")}</ListCell>
                  <ListCell>{t("État")}</ListCell>
                </div>
                <Space />
              </ListFields>

              {loading && (
                <ListItem isSmall>
                  <FontAwesomeIcon icon={faSpinner} spin className="text-gray-400 mr-2" />
                  <span className="text-gray-500">{t("Chargement de l'historique...")}</span>
                </ListItem>
              )}

              {!loading && history.length === 0 && (
                <ListItem isSmall>
                  <span className="text-gray-400 italic">{t("Aucun historique disponible")}</span>
                </ListItem>
              )}

              {!loading &&
                history.map((h, i) => {
                  // ligne précédente
                  const prev = history[i + 1]
                  const trend = getTrendIcon(h, prev)
                  const changes: JSX.Element[] = []

                  // Désactiver le highlight pour la première ligne
                  const shouldHighlight = h.sourceId !== null

                  organization.riskAttributes.forEach((key) => {
                    const diff = renderDiff(key, h.attributes?.[key], prev?.attributes?.[key], shouldHighlight)
                    if (diff) changes.push(diff)
                  })

                  const hasChanges = changes.length > 0

                  return (
                    <ListItem key={h.id} isSmall>
                      <div className="flex flex-1 items-start">
                        {/* Date */}
                        <ListCell width={140}>
                          <span className="text-gray-500 text-sm mt-[2px]">
                            {h.dateValidated ? (
                              format(new Date(h.dateValidated), "dd/MM/yyyy HH:mm", { locale: fr })
                            ) : (
                              <i className="text-sm">{t("En attente de validation")}</i>
                            )}
                          </span>
                        </ListCell>

                        {/* Colonne Mise à jour */}
                        <ListCell width={618}>
                          {hasChanges ? (
                            <div className="text-sm border-l-4 border-yellow-300">{changes}</div>
                          ) : (
                            <span className="text-gray-400 italic">{t("Aucune modification")}</span>
                          )}
                        </ListCell>

                        {/* F */}
                        <ListCell>
                          <div className="-mt-[3px]">
                            <RiskDotScale
                              risk={h}
                              id="frequency"
                              position="left"
                              highlight={shouldHighlight && h.frequency !== prev?.frequency}
                            />
                          </div>
                        </ListCell>

                        {/* G */}
                        <ListCell>
                          <div className="-mt-[3px]">
                            <RiskDotScale
                              risk={h}
                              id="severity"
                              position="left"
                              highlight={shouldHighlight && h.severity !== prev?.severity}
                            />
                          </div>
                        </ListCell>

                        {/* P */}
                        <ListCell>
                          <div className="-mt-[3px]">
                            <RiskDotScale
                              risk={h}
                              id="prevention"
                              position="left"
                              highlight={shouldHighlight && h.prevention !== prev?.prevention}
                            />
                          </div>
                        </ListCell>

                        {/* Risque brut */}
                        <ListCell width={140}>
                          <Chip isOutlineColored color={h.valueInitialColor}>
                            {h.valueInitialLabel}
                          </Chip>
                        </ListCell>

                        {/* Risque résiduel */}
                        <ListCell width={150}>
                          <Chip color={h.valueEffectiveColor}>{h.valueEffectiveLabel}</Chip>
                        </ListCell>

                        {/* État / Tendance */}
                        <ListCell>
                          <FontAwesomeIcon icon={trend} className={`text-sm ${getTrendColor(h, prev)}`} />
                        </ListCell>

                        <Space />
                      </div>
                    </ListItem>
                  )
                })}
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t flex justify-end">
            <Button onClick={onClose} className="primary">
              {t("utils_close")}
            </Button>
          </div>
        </div>
      </SlideIn>
    </div>
  )
}
